// Compare two securities side by side. Computed live on the server (any ticker works, not
// just the watchlist), reusing the same snapshot the watchlist produces. Metric rows are
// color-coded good / ok / bad where a metric has a clear "better" direction.

import type { CSSProperties, MouseEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeftRight, ChevronDown, Info } from "lucide-react";

import { ApiClientError } from "../api/client";
import { useApi } from "../api/useApi";
import { ExpensivenessComparison } from "../components/ExpensivenessComparison";
import type {
  CompareSide,
  ComparePeriodReturns,
  GrowthPoint,
  Momentum,
  RelativeValuation,
  RiskMetrics,
  WatchlistItem,
  WindowedRiskTrends,
} from "../types";
import { changeColor, money, shortName, signedPercent, sinceSpan } from "../lib/format";
import { alignGrowthSeries } from "../lib/chartMath";
import {
  fundCostScore,
  overallScore,
  ratiosScore,
  returnsScore,
  riskScore,
  valuationScores,
} from "../lib/compareMetricScores";
import {
  type RatedMetric,
  qualityColor,
  qualityLabel,
  rateMetric,
} from "../lib/metricQuality";
import {
  DEFAULT_RISK_WINDOW,
  RISK_WINDOWS,
  type RiskWindowKey,
  hasWindow,
  isRiskWindowKey,
  riskForWindow,
} from "../lib/riskWindows";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

type Side = { item: CompareSide | null };

// How to render each comparison row: how to read the value from an item, how to format it,
// and (optionally) which rated metric drives the color and which direction wins.
type Row = {
  label: string;
  hint?: string;
  // Exact thresholds behind the quality color, shown for color-coded ratios.
  colorGuide?: string;
  value: (w: CompareSide) => number | null | undefined;
  format: (v: number | null | undefined) => string;
  // The rated metric for color-coding, when the figure has a quality.
  metric?: RatedMetric;
  // For the "which side is better" emphasis: higher, lower, or none.
  better?: "higher" | "lower";
};

type MetricGroup = {
  label: string;
  rows: Row[];
  score?: (item: CompareSide) => number | null;
  scoreHint?: string;
};

const pct = (v: number | null | undefined) => (v == null ? "—" : `${v.toFixed(2)}%`);
const num = (v: number | null | undefined) => (v == null ? "—" : v.toFixed(2));

// Market snapshot rows do not depend on the selected risk window.
const MARKET_ROWS: Row[] = [
  {
    label: "Price",
    value: (w) => w.latest_price,
    format: (v) => money(v),
  },
  {
    label: "Today",
    hint: "1 day",
    value: (w) => w.today_change_percent,
    format: signedPercent,
    better: "higher",
  },
  {
    label: "Drawdown from high",
    hint: "all-time",
    value: (w) => w.drawdown_percent,
    format: signedPercent,
    better: "higher", // less negative is better
  },
];

// Risk rows for a chosen window. The accessor reads that window's metrics so switching the
// window selector re-renders every risk row against the same window for both securities.
// ``windowLabel`` (e.g. "5Y", "Max") is surfaced as each row's timeframe hint.
function riskRows(windowKey: RiskWindowKey, windowLabel: string): Row[] {
  const m = (w: WatchlistItem): RiskMetrics | undefined | null => riskForWindow(w.risk, windowKey);
  return [
    {
      label: "Volatility",
      hint: `annualized · ${windowLabel}`,
      value: (w) => m(w)?.volatility,
      format: pct,
      metric: "volatility",
      better: "lower",
    },
    {
      label: "Max drawdown",
      hint: windowLabel,
      value: (w) => m(w)?.max_drawdown,
      format: pct,
      metric: "max_drawdown",
      better: "higher", // less negative is better
    },
    { label: "Beta (vs SPY)", hint: windowLabel, value: (w) => m(w)?.beta, format: num },
    {
      label: "Annualized tracking error (vs SPY)",
      hint: windowLabel,
      value: (w) => m(w)?.tracking_error,
      format: pct,
      better: "lower",
    },
    {
      label: "Information ratio (vs SPY)",
      hint: windowLabel,
      value: (w) => m(w)?.information_ratio,
      format: num,
      better: "higher",
    },
  ];
}

function ratioRows(windowKey: RiskWindowKey, windowLabel: string): Row[] {
  const m = (w: WatchlistItem): RiskMetrics | undefined | null => riskForWindow(w.risk, windowKey);
  return [
    {
      label: "Sharpe",
      hint: windowLabel,
      value: (w) => m(w)?.sharpe,
      format: num,
      metric: "sharpe",
      better: "higher",
      colorGuide: "Green: 1.00 or higher · Amber: 0.50–0.99 · Red: below 0.50",
    },
    {
      label: "Sortino",
      hint: windowLabel,
      value: (w) => m(w)?.sortino,
      format: num,
      metric: "sortino",
      better: "higher",
      colorGuide: "Green: 1.50 or higher · Amber: 0.75–1.49 · Red: below 0.75",
    },
    {
      label: "Omega",
      hint: windowLabel,
      value: (w) => m(w)?.omega,
      format: num,
      metric: "omega",
      better: "higher",
      colorGuide: "Green: 1.20 or higher · Amber: 1.00–1.19 · Red: below 1.00",
    },
    {
      label: "Calmar",
      hint: windowLabel,
      value: (w) => m(w)?.calmar,
      format: num,
      metric: "calmar",
      better: "higher",
      colorGuide: "Green: 1.00 or higher · Amber: 0.50–0.99 · Red: below 0.50",
    },
    {
      label: "Martin",
      hint: windowLabel,
      value: (w) => m(w)?.martin,
      format: num,
      metric: "martin",
      better: "higher",
      colorGuide: "Green: 2.00 or higher · Amber: 1.00–1.99 · Red: below 1.00",
    },
  ];
}

const FUND_COST_ROWS: Row[] = [
  {
    label: "Net expense ratio",
    hint: "annual",
    value: (w) => w.expense_ratio,
    format: (v) => (v == null ? "—" : `${(v * 100).toFixed(2)}%`),
    better: "lower",
  },
];

/** Which side (a/b) holds the better value for a directional row, or null for a tie / N/A. */
function betterSide(row: Row, a: CompareSide, b: CompareSide): "a" | "b" | null {
  if (!row.better) return null;
  const va = row.value(a);
  const vb = row.value(b);
  if (va == null || vb == null || va === vb) return null;
  if (row.better === "higher") return va > vb ? "a" : "b";
  return va < vb ? "a" : "b";
}

function MetricCell({
  row,
  item,
  isBetter,
}: {
  row: Row;
  item: CompareSide;
  isBetter: boolean;
}) {
  const v = row.value(item);
  const quality = row.metric ? rateMetric(row.metric, v) : null;
  // Quality rating drives the color where a metric has one (e.g. Volatility, Sharpe).
  // Otherwise, signed-percent rows (Today, Drawdown from high) get sign-based red/green
  // like the rest of the app, so negatives read red and positives green.
  const color = quality ? qualityColor(quality) : row.format === signedPercent ? changeColor(v) : "";
  const tooltip = quality
    ? `${row.label}: ${qualityLabel(quality)}${row.colorGuide ? `. ${row.colorGuide}` : ""}`
    : undefined;
  return (
    <td className="px-1 py-2 text-right tabular-nums sm:px-3">
      <span
        className={color}
        title={tooltip}
      >
        {row.format(v)}
      </span>
      {isBetter && (
        <span className="ml-1 text-[10px] font-medium text-success" aria-label="better value">
          ▲
        </span>
      )}
    </td>
  );
}

// The compare page's period buttons, in display order. ``annualizedHint`` marks the
// multi-year periods shown as CAGR.
const PERIOD_COLUMNS: { key: keyof ComparePeriodReturns; label: string }[] = [
  { key: "one_day", label: "1D" },
  { key: "one_month", label: "1M" },
  { key: "six_month", label: "6M" },
  { key: "ytd", label: "YTD" },
  { key: "one_year", label: "1Y" },
  { key: "three_year", label: "3Y" },
  { key: "five_year", label: "5Y" },
  { key: "ten_year", label: "10Y" },
  { key: "all_time", label: "All" },
];

/** One security's row of period returns, with the better value per period marked. */
function PeriodReturnsRow({
  symbol,
  returns,
  other,
}: {
  symbol: string;
  returns: ComparePeriodReturns | undefined;
  other: ComparePeriodReturns | undefined;
}) {
  return (
    <tr className="border-b border-border/40">
      <td className="px-3 py-2 text-left font-medium">{symbol}</td>
      {PERIOD_COLUMNS.map(({ key }) => {
        const mine = returns?.[key]?.value ?? null;
        const theirs = other?.[key]?.value ?? null;
        return (
          <td key={key} className="px-3 py-2 text-right">
            <ReturnCell mine={mine} theirs={theirs} />
          </td>
        );
      })}
    </tr>
  );
}

/** One cell comparing a security's value against the other, marking the better one.
    Shared by the wide and transposed returns layouts so the "better" rule stays single. */
function ReturnCell({ mine, theirs }: { mine: number | null; theirs: number | null }) {
  const better = mine != null && theirs != null && mine !== theirs ? mine > theirs : false;
  return (
    <span className="tabular-nums">
      <span className={changeColor(mine)}>{signedPercent(mine)}</span>
      {better && (
        <span className="ml-1 text-[10px] font-medium text-success" aria-label="better value">
          ▲
        </span>
      )}
    </span>
  );
}

/** Returns-by-period module: a compact matrix of each security's return across periods.
    Wide screens show securities as rows with a period per column. Phones transpose this
    to period rows with a column per security, so the data scrolls vertically and never
    runs off the right edge. */
function ReturnsByPeriod({ a, b }: { a: CompareSide; b: CompareSide }) {
  const ra = a.analytics?.period_returns;
  const rb = b.analytics?.period_returns;
  if (!ra && !rb) return null;
  const scoreA = returnsScore(a);
  const scoreB = returnsScore(b);
  const scoreHint =
    "Blends 1Y with annualized 3Y, 5Y, 10Y, and Max returns; 0% maps to 50.";
  const footnote = (
    <p className="mt-3 text-[10px] text-muted-foreground">
      3Y, 5Y, 10Y, and All are annualized (compound growth). Shorter periods are cumulative.
      From daily total returns.
    </p>
  );
  return (
    <Card className="gap-2 py-3" data-testid="group-card-Returns">
      <CardHeader className="py-0">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">Returns</CardTitle>
          <span
            className="group relative inline-flex cursor-help text-muted-foreground"
            tabIndex={0}
            aria-label={`Returns score method: ${scoreHint}`}
          >
            <Info className="size-3.5" aria-hidden="true" />
            <span
              role="tooltip"
              className="absolute right-0 top-full z-20 mt-1 hidden w-64 rounded-md border border-border bg-popover px-2.5 py-2 text-xs font-normal text-popover-foreground shadow-md group-hover:block group-focus:block"
            >
              {scoreHint}
            </span>
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="grid grid-cols-2 gap-2">
          <GroupScoreHead
            symbol={a.symbol}
            score={scoreA}
            band={qualityBand(scoreA)}
            isBetter={scoreA != null && scoreB != null && scoreA > scoreB}
          />
          <GroupScoreHead
            symbol={b.symbol}
            score={scoreB}
            band={qualityBand(scoreB)}
            isBetter={scoreA != null && scoreB != null && scoreB > scoreA}
          />
        </div>
        {/* Phone: period rows, one column per security. No horizontal scroll. */}
        <div className="md:hidden">
          <table className="w-full text-sm" data-testid="returns-by-period-mobile">
            <thead>
              <tr className="border-b border-border">
                <th className="py-2 pr-3 text-left font-medium">Period</th>
                <th className="px-3 py-2 text-right font-medium">{a.symbol}</th>
                <th className="px-3 py-2 text-right font-medium">{b.symbol}</th>
              </tr>
            </thead>
            <tbody>
              {PERIOD_COLUMNS.map(({ key, label }) => {
                const va = ra?.[key]?.value ?? null;
                const vb = rb?.[key]?.value ?? null;
                return (
                  <tr key={key} className="border-b border-border/40">
                    <td className="py-2 pr-3 text-left font-medium text-muted-foreground">
                      {label}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <ReturnCell mine={va} theirs={vb} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <ReturnCell mine={vb} theirs={va} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Wider screens: the original security-per-row matrix. */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-sm" data-testid="returns-by-period">
            <thead>
              <tr className="border-b border-border">
                <th className="px-3 py-2 text-left font-medium">Symbol</th>
                {PERIOD_COLUMNS.map(({ key, label }) => (
                  <th key={key} className="px-3 py-2 text-right font-medium">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <PeriodReturnsRow symbol={a.symbol} returns={ra} other={rb} />
              <PeriodReturnsRow symbol={b.symbol} returns={rb} other={ra} />
            </tbody>
          </table>
        </div>
        {footnote}
      </CardContent>
    </Card>
  );
}

/** A soft background tint scaling with a return's sign and magnitude, for the yearly
    heatmap. Positive greens, negative reds, deeper with larger magnitude (capped). */
function heatStyle(value: number | null | undefined): CSSProperties {
  if (value == null) return {};
  const mag = Math.min(Math.abs(value) / 30, 1); // saturate near +/-30%
  const alpha = 0.08 + mag * 0.32;
  const hue = value >= 0 ? 150 : 0; // green vs red
  return { backgroundColor: `oklch(0.72 0.17 ${hue} / ${alpha.toFixed(2)})` };
}

/** Yearly performance module: a calendar-year heatmap for both securities. The years are
    the union across both, newest first; a cell is blank where that security has no data. */
function YearlyPerformance({ a, b }: { a: CompareSide; b: CompareSide }) {
  const [expanded, setExpanded] = useState(false);
  const ya = a.analytics?.yearly_returns ?? [];
  const yb = b.analytics?.yearly_returns ?? [];
  if (ya.length === 0 && yb.length === 0) return null;

  const years = Array.from(new Set([...ya, ...yb].map((y) => y.year))).sort((x, y) => y - x);
  const mapA = new Map(ya.map((y) => [y.year, y]));
  const mapB = new Map(yb.map((y) => [y.year, y]));

  // Keep the module compact: show only the most recent years by default and let the
  // reader expand to the full history. Newest-first, so the slice is the leading years.
  const COLLAPSED_COUNT = 5;
  const collapsible = years.length > COLLAPSED_COUNT;
  const visibleYears = expanded ? years : years.slice(0, COLLAPSED_COUNT);
  const hiddenCount = years.length - visibleYears.length;

  const heatCell = (entry: { value: number; partial: boolean } | undefined, key: string) => (
    <td
      key={key}
      className="px-3 py-2 text-right tabular-nums"
      style={heatStyle(entry?.value)}
    >
      {entry ? signedPercent(entry.value) : "—"}
    </td>
  );

  // Wide layout: one row per security, a year per column.
  const row = (symbol: string, m: Map<number, { value: number; partial: boolean }>) => (
    <tr className="border-b border-border/40">
      <td className="px-3 py-2 text-left font-medium">{symbol}</td>
      {visibleYears.map((yr) => heatCell(m.get(yr), String(yr)))}
    </tr>
  );

  const toggle = collapsible && (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      aria-expanded={expanded}
      className="mt-2 flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
    >
      <ChevronDown
        className={"size-3.5 transition-transform " + (expanded ? "rotate-180" : "")}
        aria-hidden
      />
      {expanded ? "Show fewer years" : `Show ${hiddenCount} more ${hiddenCount === 1 ? "year" : "years"}`}
    </button>
  );

  const footnote = (
    <p className="mt-3 text-[10px] text-muted-foreground">
      Calendar-year total return. The current year is through the latest close.
    </p>
  );

  return (
    <Card className="gap-3 py-3">
      <CardHeader className="py-0">
        <CardTitle className="text-base">Yearly performance</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Phone: year rows, one column per security. No horizontal scroll. */}
        <div className="md:hidden">
          <table className="w-full text-sm" data-testid="yearly-performance-mobile">
            <thead>
              <tr className="border-b border-border">
                <th className="py-2 pr-3 text-left font-medium">Year</th>
                <th className="px-3 py-2 text-right font-medium">{a.symbol}</th>
                <th className="px-3 py-2 text-right font-medium">{b.symbol}</th>
              </tr>
            </thead>
            <tbody>
              {visibleYears.map((yr) => {
                const partial = mapA.get(yr)?.partial || mapB.get(yr)?.partial;
                return (
                  <tr key={yr} className="border-b border-border/40">
                    <td className="py-2 pr-3 text-left font-medium text-muted-foreground">
                      {yr}
                      {partial && (
                        <span className="text-[10px] text-muted-foreground"> YTD</span>
                      )}
                    </td>
                    {heatCell(mapA.get(yr), `a-${yr}`)}
                    {heatCell(mapB.get(yr), `b-${yr}`)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Wider screens: the original year-per-column heatmap. */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-sm" data-testid="yearly-performance">
            <thead>
              <tr className="border-b border-border">
                <th className="px-3 py-2 text-left font-medium">Symbol</th>
                {visibleYears.map((yr) => {
                  const partial = mapA.get(yr)?.partial || mapB.get(yr)?.partial;
                  return (
                    <th key={yr} className="px-3 py-2 text-right font-medium">
                      {yr}
                      {partial && (
                        <span className="text-[10px] text-muted-foreground"> YTD</span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {row(a.symbol, mapA)}
              {row(b.symbol, mapB)}
            </tbody>
          </table>
        </div>
        {toggle}
        {footnote}
      </CardContent>
    </Card>
  );
}

/** A 0-100 score as a small SVG donut dial, colored by band. ``size`` scales the whole
    dial so the same component serves the compact group cards and the larger overall card. */
function ScoreDial({
  score,
  size = 56,
  label = "score",
}: {
  score: number | null | undefined;
  size?: number;
  label?: string;
}) {
  const value = score ?? 0;
  const c = size / 2; // center
  const r = size * 0.357; // stroke-inset radius (20 at size 56)
  const sw = size * 0.107; // stroke width (6 at size 56)
  const circ = 2 * Math.PI * r;
  const frac = Math.max(0, Math.min(1, value / 100));
  const color = value >= 67 ? "var(--success)" : value >= 34 ? "var(--warning)" : "var(--destructive)";
  const textSize = size >= 72 ? "text-base" : "text-sm";
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`${label} ${score ?? "n/a"}`}
    >
      <circle cx={c} cy={c} r={r} fill="none" stroke="var(--border)" strokeWidth={sw} />
      {score != null && (
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeDasharray={`${(circ * frac).toFixed(2)} ${circ.toFixed(2)}`}
          transform={`rotate(-90 ${c} ${c})`}
        />
      )}
      <text
        x={c}
        y={c + size * 0.07}
        textAnchor="middle"
        className={`fill-foreground font-semibold ${textSize}`}
      >
        {score ?? "—"}
      </text>
    </svg>
  );
}

/** A labeled trend metric (percent), colored by sign. */
function TrendStat({ label, value }: { label: string; value: number | null | undefined }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={"tabular-nums " + changeColor(value)}>{signedPercent(value)}</div>
    </div>
  );
}

/** Generic quality band for a 0-100 score, used by the group and overall cards. */
function qualityBand(score: number | null | undefined): string {
  if (score == null) return "No data";
  if (score >= 67) return "Strong";
  if (score >= 34) return "Mixed";
  return "Weak";
}

/** Tailwind text color for a score band. */
function scoreTone(score: number | null | undefined): string {
  if (score == null) return "text-muted-foreground";
  return score >= 67 ? "text-success" : score >= 34 ? "text-warning" : "text-destructive";
}

/** Four historical trend measures for one security. */
function TrendBlock({ symbol, m }: { symbol: string; m: Momentum | undefined | null }) {
  return (
    <div className="space-y-2" data-testid="trend-block">
      <div className="text-sm font-medium">{symbol}</div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <TrendStat label="6-month return" value={m?.six_month} />
        <TrendStat label="12-month return" value={m?.twelve_month} />
        <TrendStat label="vs 200-day average" value={m?.price_vs_200d_ma} />
        <TrendStat label="vs 52-week high" value={m?.price_vs_52w_high} />
      </div>
    </div>
  );
}

function TrendComparison({ a, b }: { a: CompareSide; b: CompareSide }) {
  const ma = a.analytics?.momentum;
  const mb = b.analytics?.momentum;
  if (!ma && !mb) return null;
  return (
    <Card className="gap-3 py-3" data-testid="trend-comparison">
      <CardHeader className="py-0">
        <CardTitle className="text-base">Trend</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <TrendBlock symbol={a.symbol} m={ma} />
        <div className="border-t border-border/40" />
        <TrendBlock symbol={b.symbol} m={mb} />
        <p className="text-[10px] text-muted-foreground">
          Historical returns and price trends. These measures do not predict future returns.
        </p>
      </CardContent>
    </Card>
  );
}

function valuationTone(label: RelativeValuation["label"]): string {
  if (label === "cheap") return "text-success";
  if (label === "expensive") return "text-destructive";
  return "text-muted-foreground";
}

function ValuationBlock({
  symbol,
  value,
  cheaper,
}: {
  symbol: string;
  value: RelativeValuation | null | undefined;
  cheaper: boolean;
}) {
  const percentile = value?.stretch_percentile;
  const label = value?.label;
  return (
    <div className="space-y-3" data-testid={`valuation-${symbol}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium">{symbol}</div>
          <div className={"text-xs font-semibold capitalize " + valuationTone(label)}>
            {label ?? "Not enough history"}
            {cheaper && <span className="ml-1 text-success">· cheaper relative stretch</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-semibold tabular-nums">
            {percentile == null ? "—" : `${percentile.toFixed(0)}th`}
          </div>
          <div className="text-[10px] text-muted-foreground">stretch percentile</div>
        </div>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true">
        <div
          className={
            "h-full rounded-full transition-[width] " +
            (label === "cheap"
              ? "bg-success"
              : label === "expensive"
                ? "bg-destructive"
                : "bg-warning")
          }
          style={{ width: `${Math.max(0, Math.min(100, percentile ?? 0))}%` }}
        />
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs">
        <div>
          <div className="text-muted-foreground">vs 200d MA</div>
          <div className="font-medium tabular-nums">{signedPercent(value?.price_vs_200d_ma)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">vs 52w high</div>
          <div className="font-medium tabular-nums">{signedPercent(value?.price_vs_52w_high)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Stretch z-score</div>
          <div className="font-medium tabular-nums">{num(value?.stretch_zscore)}</div>
        </div>
      </div>
    </div>
  );
}

function RelativeValuationComparison({ a, b }: { a: CompareSide; b: CompareSide }) {
  const va = a.analytics?.relative_valuation;
  const vb = b.analytics?.relative_valuation;
  if (!va && !vb) return null;
  const pa = va?.stretch_percentile;
  const pb = vb?.stretch_percentile;
  const aCheaper = pa != null && pb != null && pa < pb;
  const bCheaper = pa != null && pb != null && pb < pa;
  return (
    <Card className="gap-3 py-3" data-testid="relative-valuation">
      <CardHeader className="py-0">
        <CardTitle className="text-base">Price trend stretch</CardTitle>
        <CardDescription>
          Lower is cheaper relative to the security&apos;s own history.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 pt-0 md:grid-cols-2">
        <ValuationBlock symbol={a.symbol} value={va} cheaper={aCheaper} />
        <ValuationBlock symbol={b.symbol} value={vb} cheaper={bCheaper} />
        <p className="text-[10px] text-muted-foreground md:col-span-2">
          The percentile ranks today&apos;s price-to-200-day-average gap against up to three
          years of the same security&apos;s daily gaps. Cheap is the bottom quartile; expensive
          is the top quartile. This is a price-history measure—not P/E, P/B, holdings
          fundamental valuation, intrinsic value, or a buy/sell signal.
        </p>
      </CardContent>
    </Card>
  );
}

const VALUATION_ROWS = [
  { field: "price_to_earnings", label: "P/E (trailing)" },
  { field: "price_to_book", label: "P/B" },
  { field: "price_to_sales", label: "P/S (trailing)" },
  { field: "price_to_cash_flow", label: "P/CF" },
  { field: "enterprise_to_ebitda", label: "EV/EBITDA" },
  { field: "forward_price_to_earnings", label: "Forward P/E (estimate)" },
  { field: "peg_ratio", label: "PEG (provider reported)" },
] as const;

function ValuationComparison({ a, b }: { a: CompareSide; b: CompareSide }) {
  const scores = valuationScores(a, b);
  const showEstimates = a.valuation_kind === "company" || b.valuation_kind === "company";
  const rows = VALUATION_ROWS.filter(({ field }) =>
    showEstimates || (field !== "forward_price_to_earnings" && field !== "peg_ratio"
      && field !== "enterprise_to_ebitda"));
  const sourceLine = (item: CompareSide, field: typeof VALUATION_ROWS[number]["field"]) => {
    const rawDate = item[`${field}_as_of`];
    const date = rawDate ? new Date(rawDate) : null;
    const refreshed = date && Number.isFinite(date.getTime())
      ? ` · retrieved ${new Intl.DateTimeFormat(undefined, {
          month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
        }).format(date)}` : "";
    const calculated = item[`${field}_source`] === "calculated_yahoo_finance";
    return `${calculated ? "Calculated from Yahoo Finance" : "Yahoo Finance"}${refreshed}`;
  };
  const label = (score: number | null) => score == null
    ? "Insufficient comparable data" : score > 50 ? "Lower relative valuation"
      : score < 50 ? "Higher relative valuation" : "Similar relative valuation";
  return (
    <Card className="gap-3 py-3" data-testid="portfolio-valuation">
      <CardHeader className="py-0">
        <CardTitle className="text-base">Valuation</CardTitle>
        <CardDescription>Published company fundamentals or aggregate equity-holdings ratios for funds.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="grid gap-3 sm:grid-cols-2">
          {[a, b].map((item, index) => {
            const score = index === 0 ? scores.a : scores.b;
            return (
              <div key={item.symbol} className="space-y-3 rounded-md border border-border/60 p-3"
                data-testid={`fundamentals-${item.symbol}`}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium">{item.symbol}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {item.valuation_kind === "company" ? "Company" : item.valuation_kind === "portfolio" ? "Equity holdings" : "Valuation data"}
                    </div>
                  </div>
                  {score != null && <ScoreDial score={score} label={`${item.symbol} relative valuation score`} />}
                </div>
                <div className="text-xs text-muted-foreground">{label(score)}</div>
                <dl className="space-y-2">
                  {rows.map(({ field, label: metric }) => {
                    const value = item[field];
                    const available = value != null && Number.isFinite(value) && value > 0;
                    return (
                      <div key={field}>
                        <div className="flex justify-between gap-2 text-xs">
                          <dt className="text-muted-foreground">{metric}</dt>
                          <dd className="font-medium tabular-nums">{available ? `${value.toFixed(2)}x` : "—"}</dd>
                        </div>
                        {available && <div className="text-[10px] text-muted-foreground">{sourceLine(item, field)}</div>}
                      </div>
                    );
                  })}
                </dl>
                {!VALUATION_ROWS.some(({ field }) => item[field] != null) && (
                  <p className="text-xs text-muted-foreground">
                    {item.valuation_status === "unavailable"
                      ? "The source has no usable valuation ratios for this security."
                      : "Valuation data is pending a scheduled refresh."}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-muted-foreground">
          Relative valuation score: equal-weight comparison of shared positive P/E, P/B, P/S and P/CF;
          at least two ratios and matching company/fund types are required. Each ratio scores
          100 × the other security&apos;s ratio ÷ the sum of both ratios. 50 means equal;
          higher means cheaper relative to the other security. Estimates are excluded.
          Sector, growth and fund methodology differences can explain lower ratios.
          This score is separate from overall quality and does not estimate fair value.
        </p>
        <p className="text-[10px] text-muted-foreground">
          Best-effort ratios supplied by Yahoo Finance. Dates show retrieval time;
          underlying reporting periods may differ. Missing or non-positive ratios are unavailable.
          {scores.a != null && ` Score uses ${scores.metrics.join(", ")}.`}
        </p>
      </CardContent>
    </Card>
  );
}

/** Build an SVG polyline path for a series of values scaled to a width x height box. */
function linePath(values: number[], width: number, height: number, pad = 1): string {
  if (values.length === 0) return "";
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const range = hi - lo || 1;
  const n = values.length;
  return values
    .map((v, i) => {
      const x = n === 1 ? width / 2 : (i / (n - 1)) * (width - 2 * pad) + pad;
      const y = height - pad - ((v - lo) / range) * (height - 2 * pad);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

/** A small inline sparkline. */
function Sparkline({ values, width = 90, height = 24 }: { values: number[]; width?: number; height?: number }) {
  if (values.length < 2) {
    return <span className="text-[10px] text-muted-foreground">—</span>;
  }
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <path d={linePath(values, width, height)} fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

// One-line description per ratio, keyed by the metric field. Shown under each ratio name.
const RATIO_DESCRIPTIONS: Record<string, string> = {
  sharpe: "Return per unit of total volatility",
  sortino: "Return per unit of downside risk",
  omega: "Gain probability vs loss probability",
  calmar: "Return relative to maximum drawdown",
  martin: "Return relative to average drawdown",
};

// Rolling trend windows the backend emits, longest to shortest. A rolling trend needs the
// base window shorter than the available history, so "max" is not among them.
const TREND_WINDOWS_LONGEST_FIRST: RiskWindowKey[] = [
  "ten_year",
  "five_year",
  "three_year",
  "one_year",
];

/** Which trend window to draw sparklines from for a security: the selected window when it
    has trend data, otherwise the longest available one. Lets Max (no rolling trend of its
    own) still show the longest rolling trend instead of a blank. Returns null when the
    security has no trend data at all. */
function effectiveTrendWindow(
  trends: WindowedRiskTrends | undefined | null,
  windowKey: RiskWindowKey,
): RiskWindowKey | null {
  const has = (key: RiskWindowKey) => (trends?.[key]?.length ?? 0) > 0;
  if (has(windowKey)) return windowKey;
  return TREND_WINDOWS_LONGEST_FIRST.find(has) ?? null;
}

/** The rolling sparkline series for a ratio in a given trend window, for one security. */
function ratioSpark(
  trends: WindowedRiskTrends | undefined | null,
  windowKey: RiskWindowKey | null,
  metricKey: string,
): number[] {
  if (windowKey == null) return [];
  const list = trends?.[windowKey];
  return list?.find((t) => t.key === metricKey)?.spark ?? [];
}

/** Risk-adjusted ratios card: the five ratios for the selected risk window, each with
    both securities' latest value (quality-colored, winner marked), a rolling sparkline,
    a description, and a difference column. Merges the former "Risk-adjusted ratios" group
    card and the separate "Risk-adjusted trends" module, which showed the same ratios
    twice. Driven by the top risk window. Sparklines appear where rolling trend data
    exists (1Y/3Y/5Y/10Y); the Max window shows values without a sparkline. */
function RiskRatiosCard({
  a,
  b,
  rows,
  windowKey,
  windowLabel,
  scoreA,
  scoreB,
}: {
  a: CompareSide;
  b: CompareSide;
  rows: Row[];
  windowKey: RiskWindowKey;
  windowLabel: string;
  scoreA: number | null;
  scoreB: number | null;
}) {
  const ta = a.analytics?.risk_trends ?? undefined;
  const tb = b.analytics?.risk_trends ?? undefined;
  const scoreHint = "Equal blend of Sharpe, Sortino, Omega, Calmar, and Martin ratios.";

  // Values track the selected window. Sparklines use that window's rolling trend when it
  // has one, otherwise the longest available (so Max still shows a trend line). The label
  // reflects the trend window actually drawn when it differs from the selection.
  const trendKeyA = effectiveTrendWindow(ta, windowKey);
  const trendKeyB = effectiveTrendWindow(tb, windowKey);
  const drawnTrendKey = trendKeyA ?? trendKeyB;
  const drawnTrendLabel = RISK_WINDOWS.find((w) => w.key === drawnTrendKey)?.label ?? null;
  const trendDiffers = drawnTrendKey != null && drawnTrendKey !== windowKey;

  // The value cell: latest number (quality-colored), a winner marker, and a sparkline.
  const valueCell = (row: Row, mine: CompareSide, winner: boolean, spark: number[]) => {
    const v = row.value(mine);
    const quality = row.metric ? rateMetric(row.metric, v) : null;
    return (
      <td className="px-1 py-2 text-right sm:px-3">
        <div className="flex items-center justify-end gap-2">
          {spark.length >= 2 && (
            <span className="hidden text-muted-foreground sm:inline-flex">
              <Sparkline values={spark} />
            </span>
          )}
          <span className={"tabular-nums " + qualityColor(quality)}>{row.format(v)}</span>
          {winner && (
            <span className="text-[10px] font-medium text-success" aria-label="better value">
              ▲
            </span>
          )}
        </div>
      </td>
    );
  };

  return (
    <Card className="gap-2 py-3" data-testid="group-card-Risk-adjusted ratios">
      <CardHeader className="py-0">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">Risk-adjusted ratios</CardTitle>
          <span
            className="group relative inline-flex cursor-help text-muted-foreground"
            tabIndex={0}
            aria-label={`Risk-adjusted ratios score method: ${scoreHint}`}
          >
            <Info className="size-3.5" aria-hidden="true" />
            <span
              role="tooltip"
              className="absolute right-0 top-full z-20 mt-1 hidden w-64 rounded-md border border-border bg-popover px-2.5 py-2 text-xs font-normal text-popover-foreground shadow-md group-hover:block group-focus:block"
            >
              {scoreHint}
            </span>
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="grid grid-cols-2 gap-2">
          <GroupScoreHead
            symbol={a.symbol}
            score={scoreA}
            band={qualityBand(scoreA)}
            isBetter={scoreA != null && scoreB != null && scoreA > scoreB}
          />
          <GroupScoreHead
            symbol={b.symbol}
            score={scoreB}
            band={qualityBand(scoreB)}
            isBetter={scoreA != null && scoreB != null && scoreB > scoreA}
          />
        </div>
        <table className="w-full text-sm" data-testid="risk-trends">
          <thead>
            <tr className="border-b border-border text-[10px] uppercase tracking-wide text-muted-foreground">
              <th className="px-1 py-1 text-left font-medium sm:px-3">Metric</th>
              <th className="px-1 py-1 text-right font-medium sm:px-3">{a.symbol}</th>
              <th className="px-1 py-1 text-right font-medium sm:px-3">{b.symbol}</th>
              <th className="px-1 py-1 text-right font-medium sm:px-3">Diff</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const key = row.metric ?? "";
              const va = row.value(a);
              const vb = row.value(b);
              const winner = betterSide(row, a, b);
              const diff = va != null && vb != null ? va - vb : null;
              return (
                <tr key={row.label} className="border-b border-border/30 align-middle last:border-0">
                  <td className="px-1 py-2 text-left sm:px-3">
                    <div className="font-medium">
                      <MetricLabel row={{ ...row, hint: undefined }} />
                    </div>
                    {RATIO_DESCRIPTIONS[key] && (
                      <div className="text-[10px] text-muted-foreground">
                        {RATIO_DESCRIPTIONS[key]}
                      </div>
                    )}
                  </td>
                  {valueCell(row, a, winner === "a", ratioSpark(ta, trendKeyA, key))}
                  {valueCell(row, b, winner === "b", ratioSpark(tb, trendKeyB, key))}
                  <td className={"px-1 py-2 text-right tabular-nums sm:px-3 " + changeColor(diff)}>
                    {diff != null ? (diff >= 0 ? "+" : "") + diff.toFixed(2) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="text-[10px] text-muted-foreground">
          Each ratio on the selected {windowLabel} window. Higher is better for all five.
          {trendDiffers
            ? ` Sparklines show the rolling ${drawnTrendLabel} trend (no rolling trend for ${windowLabel}).`
            : " Sparklines show the rolling trend where available."}
        </p>
      </CardContent>
    </Card>
  );
}

// Growth chart range tabs. Keys match the server's per-range growth series, each computed
// from the full daily series sliced to that exact window (accurate start and end).
const GROWTH_RANGES: { key: string; label: string }[] = [
  { key: "max", label: "Max" },
  { key: "10y", label: "10Y" },
  { key: "5y", label: "5Y" },
  { key: "3y", label: "3Y" },
  { key: "1y", label: "1Y" },
  { key: "ytd", label: "YTD" },
  { key: "6m", label: "6M" },
];

const GROWTH_RANGE_FOR_RISK_WINDOW: Record<RiskWindowKey, string> = {
  one_year: "1y",
  three_year: "3y",
  five_year: "5y",
  ten_year: "10y",
  max: "max",
};

// Chart geometry (viewBox units). Padding keeps lines off the edges.
const CHART_W = 640;
const CHART_H = 220;
const CHART_PAD = 2;

/** x pixel for index i of a length-n series across the chart width. */
function chartX(i: number, n: number): number {
  if (n <= 1) return CHART_W / 2;
  return (i / (n - 1)) * (CHART_W - 2 * CHART_PAD) + CHART_PAD;
}

/** y pixel for a value given the shared [lo, hi] scale. */
function chartY(value: number, lo: number, hi: number): number {
  const range = hi - lo || 1;
  return CHART_H - CHART_PAD - ((value - lo) / range) * (CHART_H - 2 * CHART_PAD);
}

/** Dollar value of a $10k investment given a percent change. */
function growthDollars(pct: number): string {
  return money(10000 * (1 + pct / 100));
}

function MetricLabel({ row }: { row: Row }) {
  return (
    <>
      {row.label}
      {row.hint && (
        <span className="ml-1 text-[10px] text-muted-foreground">{row.hint}</span>
      )}
      {row.colorGuide && (
        <span
          className="group relative ml-1.5 inline-flex cursor-help align-middle text-muted-foreground"
          tabIndex={0}
          aria-label={`${row.label} color guide: ${row.colorGuide}`}
        >
          <Info className="size-3.5" aria-hidden="true" />
          <span
            role="tooltip"
            className="absolute left-0 top-full z-20 mt-1 hidden w-64 rounded-md border border-border bg-popover px-2.5 py-2 text-xs font-normal normal-case tracking-normal text-popover-foreground shadow-md group-hover:block group-focus:block"
          >
            {row.colorGuide}
          </span>
        </span>
      )}
    </>
  );
}

/** One security's header inside a group card: a compact dial, the group score, and band. */
function GroupScoreHead({
  symbol,
  score,
  band,
  isBetter,
}: {
  symbol: string;
  score: number | null;
  band: string;
  isBetter: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <ScoreDial score={score} size={44} label={`${symbol} score`} />
      <div className="min-w-0">
        <div className="flex items-center gap-1 text-sm font-medium">
          {symbol}
          {isBetter && (
            <span className="text-[10px] text-success" aria-label="better score">
              ▲
            </span>
          )}
        </div>
        <div className={"text-[11px] " + scoreTone(score)}>{band}</div>
      </div>
    </div>
  );
}

/** A single metric group rendered as its own compact card: title, each security's group
    score with a dial, then the group's rows as a tight A-vs-B grid. Replaces the old
    section-header-in-one-big-table layout so each group reads on its own and stacks
    densely. ``score`` is optional for groups like the market snapshot. */
function MetricGroupCard({
  group,
  a,
  b,
}: {
  group: MetricGroup;
  a: CompareSide;
  b: CompareSide;
}) {
  const scoreA = group.score ? group.score(a) : null;
  const scoreB = group.score ? group.score(b) : null;
  const scored = group.score != null;

  return (
    <Card className="gap-2 py-3" data-testid={`group-card-${group.label}`}>
      <CardHeader className="py-0">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm">{group.label}</CardTitle>
          {scored && group.scoreHint && (
            <span
              className="group relative inline-flex cursor-help text-muted-foreground"
              tabIndex={0}
              aria-label={`${group.label} score method: ${group.scoreHint}`}
            >
              <Info className="size-3.5" aria-hidden="true" />
              <span
                role="tooltip"
                className="absolute right-0 top-full z-20 mt-1 hidden w-64 rounded-md border border-border bg-popover px-2.5 py-2 text-xs font-normal text-popover-foreground shadow-md group-hover:block group-focus:block"
              >
                {group.scoreHint}
              </span>
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {scored && (
          <div className="grid grid-cols-2 gap-2">
            <GroupScoreHead
              symbol={a.symbol}
              score={scoreA}
              band={qualityBand(scoreA)}
              isBetter={scoreA != null && scoreB != null && scoreA > scoreB}
            />
            <GroupScoreHead
              symbol={b.symbol}
              score={scoreB}
              band={qualityBand(scoreB)}
              isBetter={scoreA != null && scoreB != null && scoreB > scoreA}
            />
          </div>
        )}
        <table className="w-full table-fixed text-sm">
          <thead>
            <tr className="border-b border-border/60 text-[10px] uppercase tracking-wide text-muted-foreground">
              <th className="w-1/3 py-1 text-left font-medium">Metric</th>
              <th className="py-1 text-right font-medium">{a.symbol}</th>
              <th className="py-1 text-right font-medium">{b.symbol}</th>
            </tr>
          </thead>
          <tbody>
            {group.rows.map((row) => {
              const winner = betterSide(row, a, b);
              return (
                <tr key={row.label} className="border-b border-border/30 last:border-0">
                  <td className="py-1.5 text-left">
                    <MetricLabel row={row} />
                  </td>
                  <MetricCell row={row} item={a} isBetter={winner === "a"} />
                  <MetricCell row={row} item={b} isBetter={winner === "b"} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

/** The headline card: one combined 0-100 verdict per security, blending the group scores.
    Sits above the group cards so the overall read is the first thing shown. */
function OverallScoreCard({
  a,
  b,
  scoreA,
  scoreB,
}: {
  a: CompareSide;
  b: CompareSide;
  scoreA: number | null;
  scoreB: number | null;
}) {
  const side = (item: CompareSide, score: number | null, isBetter: boolean) => (
    <div className="flex items-center gap-3">
      <ScoreDial score={score} size={72} label={`${item.symbol} overall score`} />
      <div className="min-w-0">
        <div className="flex items-center gap-1 text-sm font-medium">
          {item.symbol}
          {isBetter && (
            <span className="text-[10px] text-success" aria-label="better overall score">
              ▲
            </span>
          )}
        </div>
        {item.name && (
          <div className="truncate text-[11px] text-muted-foreground" title={item.name}>
            {shortName(item.name, null)}
          </div>
        )}
        <div className={"text-sm " + scoreTone(score)}>{qualityBand(score)}</div>
        <div className="text-[10px] text-muted-foreground">Overall</div>
      </div>
    </div>
  );
  return (
    <Card className="gap-2 py-3" data-testid="overall-score-card">
      <CardHeader className="py-0">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">Overall score</CardTitle>
          <span
            className="group relative inline-flex cursor-help text-muted-foreground"
            tabIndex={0}
            aria-label="Overall score method: equal-weight blend of the returns, fund cost, risk, and risk-adjusted ratio group scores, on the selected risk window. Trend measures are excluded."
          >
            <Info className="size-3.5" aria-hidden="true" />
            <span
              role="tooltip"
              className="absolute right-0 top-full z-20 mt-1 hidden w-64 rounded-md border border-border bg-popover px-2.5 py-2 text-xs font-normal text-popover-foreground shadow-md group-hover:block group-focus:block"
            >
              Equal-weight blend of the group scores below (returns, fund cost, risk,
              risk-adjusted ratios) on the selected risk window. Trend measures are
              descriptive and excluded from this score.
            </span>
          </span>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3 pt-0">
        {side(a, scoreA, scoreA != null && scoreB != null && scoreA > scoreB)}
        {side(b, scoreB, scoreA != null && scoreB != null && scoreB > scoreA)}
      </CardContent>
    </Card>
  );
}

/** Annualized compound return over the exact date span drawn for one line. */
function seriesCagr(values: GrowthPoint[]): number | null {
  if (values.length < 2) return null;
  const years = (new Date(values.at(-1)!.date).getTime() - new Date(values[0].date).getTime()) /
    (365.2425 * 86_400_000);
  const growth = 1 + values.at(-1)!.pct / 100;
  if (years <= 0 || growth <= 0) return null;
  return (growth ** (1 / years) - 1) * 100;
}

function cagrLabel(value: number | null): string {
  return value == null || !Number.isFinite(value)
    ? "CAGR —"
    : `CAGR ${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
}

/** Growth-of-investment performance chart: two rebased total-return lines with a hover
    crosshair showing each series' value at the cursor's date. */
function GrowthChart({
  a,
  b,
  initialWindow,
}: {
  a: CompareSide;
  b: CompareSide;
  initialWindow: RiskWindowKey;
}) {
  // Start from the user's risk lens. This chart remains independently adjustable.
  const [range, setRange] = useState(() => GROWTH_RANGE_FOR_RISK_WINDOW[initialWindow]);
  // Hover index into the (evenly-spaced) series, or null when not hovering. Both series
  // are indexed by the same fractional position, matching how the lines are drawn.
  const [hover, setHover] = useState<number | null>(null);
  const growthA = a.analytics?.growth ?? {};
  const growthB = b.analytics?.growth ?? {};
  const hasAny = Object.values(growthA).some((s) => s.length) ||
    Object.values(growthB).some((s) => s.length);
  if (!hasAny) return null;

  // Each range is an accurate, server-computed series rebased to its true window start.
  const [sa, sb] = alignGrowthSeries(growthA[range] ?? [], growthB[range] ?? []);
  // Earliest data point across both series, for the Max-range "earliest data" caption.
  const earliest = [sa[0]?.date, sb[0]?.date].filter(Boolean).sort()[0];
  const maxSpan = sinceSpan(earliest);
  const allPct = [...sa, ...sb].map((p) => p.pct);
  const lastA = sa.at(-1)?.pct;
  const lastB = sb.at(-1)?.pct;

  const lo = allPct.length ? Math.min(...allPct, 0) : 0;
  const hi = allPct.length ? Math.max(...allPct, 0) : 1;
  const scale = (vals: GrowthPoint[]) =>
    linePathScaled(vals.map((p) => p.pct), CHART_W, CHART_H, lo, hi);

  // The longer series drives the crosshair resolution; map a hover fraction to each
  // series' nearest index so a dot always sits on the drawn line.
  const steps = Math.max(sa.length, sb.length);
  const indexFor = (series: GrowthPoint[]) =>
    series.length === 0 || hover == null
      ? null
      : Math.min(series.length - 1, Math.round((hover / Math.max(1, steps - 1)) * (series.length - 1)));
  const idxA = indexFor(sa);
  const idxB = indexFor(sb);
  const hoverX = hover == null ? null : chartX(hover, steps);
  const ptA = idxA != null ? sa[idxA] : null;
  const ptB = idxB != null ? sb[idxB] : null;
  const hoverDate = (ptA ?? ptB)?.date;

  const onMove = (e: MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0) return;
    const frac = (e.clientX - rect.left) / rect.width;
    const i = Math.round(Math.max(0, Math.min(1, frac)) * (steps - 1));
    setHover(i);
  };

  return (
    <Card className="gap-3 py-3">
      <CardHeader className="flex flex-col gap-2 py-0 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="text-base">Performance ($10k growth)</CardTitle>
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="chart range">
          {GROWTH_RANGES.map((r) => {
            const available =
              (growthA[r.key]?.length ?? 0) > 0 || (growthB[r.key]?.length ?? 0) > 0;
            return (
              <button
                key={r.key}
                type="button"
                role="tab"
                aria-selected={r.key === range}
                disabled={!available}
                aria-label={`chart-range-${r.key}`}
                onClick={() => setRange(r.key)}
                className={
                  "rounded px-2 py-0.5 text-xs font-medium transition-colors " +
                  (r.key === range
                    ? "bg-primary/15 text-foreground"
                    : available
                      ? "text-muted-foreground hover:text-foreground"
                      : "cursor-not-allowed text-muted-foreground/40")
                }
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </CardHeader>
      <CardContent className="pt-0" data-testid="growth-chart">
        {/* Tooltip: shows the hovered date and each series' value, or the latest when idle. */}
        <div className="mb-2 min-h-[2.5rem] text-xs" data-testid="growth-tooltip">
          <div className="text-[10px] text-muted-foreground">
            {hoverDate ?? "Latest"}
          </div>
          <div className="flex flex-wrap gap-4">
            <span className="flex items-center gap-1.5">
              <span className="inline-block size-2 rounded-full bg-primary" />
              {a.symbol}{" "}
              <span className="tabular-nums">{growthDollars(ptA?.pct ?? lastA ?? 0)}</span>
              <span className={"tabular-nums " + changeColor(ptA?.pct ?? lastA)}>
                {signedPercent(ptA?.pct ?? lastA)}
              </span>
              <span className="text-muted-foreground">{cagrLabel(seriesCagr(sa))}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block size-2 rounded-full bg-muted-foreground" />
              {b.symbol}{" "}
              <span className="tabular-nums">{growthDollars(ptB?.pct ?? lastB ?? 0)}</span>
              <span className={"tabular-nums " + changeColor(ptB?.pct ?? lastB)}>
                {signedPercent(ptB?.pct ?? lastB)}
              </span>
              <span className="text-muted-foreground">{cagrLabel(seriesCagr(sb))}</span>
            </span>
          </div>
        </div>
        <svg
          width="100%"
          viewBox={`0 0 ${CHART_W} ${CHART_H}`}
          className="cursor-crosshair overflow-visible"
          role="img"
          aria-label="Comparative growth chart. Hover to inspect values by date."
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        >
          <line x1="0" y1={CHART_H - 1} x2={CHART_W} y2={CHART_H - 1} stroke="var(--border)" strokeWidth="1" />
          {/* Hover crosshair: a vertical guide line plus a dot on each series. */}
          {hoverX != null && (
            <line
              data-testid="growth-chart-crosshair"
              x1={hoverX}
              y1={0}
              x2={hoverX}
              y2={CHART_H}
              stroke="var(--muted-foreground)"
              strokeWidth="1"
              strokeDasharray="3 3"
              opacity="0.6"
            />
          )}
          <path d={scale(sa)} fill="none" stroke="var(--primary)" strokeWidth="1.5" />
          <path d={scale(sb)} fill="none" stroke="var(--muted-foreground)" strokeWidth="1.5" />
          {hoverX != null && ptA && idxA != null && (
            <circle cx={hoverX} cy={chartY(ptA.pct, lo, hi)} r="3" fill="var(--primary)" />
          )}
          {hoverX != null && ptB && idxB != null && (
            <circle cx={hoverX} cy={chartY(ptB.pct, lo, hi)} r="3" fill="var(--muted-foreground)" />
          )}
        </svg>
        <p className="mt-2 text-[10px] text-muted-foreground">
          Hypothetical growth from the shared window start, dividends reinvested. Both lines
          use the same date span and scale. Hover to read values at a date.
          {range === "max" && maxSpan && ` Earliest data ${maxSpan}.`}
        </p>
      </CardContent>
    </Card>
  );
}

/** Line path with an explicit shared value range (for co-plotting two series). */
function linePathScaled(
  values: number[],
  width: number,
  height: number,
  lo: number,
  hi: number,
  pad = 2,
): string {
  if (values.length === 0) return "";
  const range = hi - lo || 1;
  const n = values.length;
  return values
    .map((v, i) => {
      const x = n === 1 ? width / 2 : (i / (n - 1)) * (width - 2 * pad) + pad;
      const y = height - pad - ((v - lo) / range) * (height - 2 * pad);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

export function Compare() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const [symbolA, setSymbolA] = useState(params.get("a")?.toUpperCase() ?? "");
  const [symbolB, setSymbolB] = useState(params.get("b")?.toUpperCase() ?? "");
  const [sideA, setSideA] = useState<Side>({ item: null });
  const [sideB, setSideB] = useState<Side>({ item: null });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  // The saved risk window seeds every metric module with a selectable time window.
  const [windowKey, setWindowKey] = useState<RiskWindowKey>(DEFAULT_RISK_WINDOW);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [preferenceStatus, setPreferenceStatus] = useState("");

  useEffect(() => {
    let active = true;
    void api.getUserPreferences()
      .then((preferences) => {
        if (active && isRiskWindowKey(preferences.risk_window)) {
          setWindowKey(preferences.risk_window);
        }
      })
      .catch(() => {
        // Existing users and temporary API failures both fall back to 1Y.
      })
      .finally(() => {
        if (active) setPreferencesLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [api]);

  const selectRiskWindow = useCallback((next: RiskWindowKey) => {
    setWindowKey(next);
    setPreferenceStatus("");
    void api.updateUserPreferences({ risk_window: next }).catch(() => {
      setPreferenceStatus("The risk window changed for this session but could not be saved.");
    });
  }, [api]);

  const runCompare = useCallback(
    async (a: string, b: string) => {
      const ta = a.trim().toUpperCase();
      const tb = b.trim().toUpperCase();
      if (!ta || !tb) {
        setStatus("Enter two symbols to compare.");
        return;
      }
      setLoading(true);
      setStatus("");
      try {
        const result = await api.compareWatchlist(ta, tb);
        setSideA({ item: result.a });
        setSideB({ item: result.b });
        setParams({ a: ta, b: tb }, { replace: true });
      } catch (err) {
        const message =
          err instanceof ApiClientError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Could not compare those symbols";
        setStatus(message);
        setSideA({ item: null });
        setSideB({ item: null });
      } finally {
        setLoading(false);
      }
    },
    [api, setParams],
  );

  // Run once on mount if both symbols came in via the URL (shareable comparison link).
  useEffect(() => {
    const a = params.get("a");
    const b = params.get("b");
    if (a && b) void runCompare(a, b);
    // Only on first mount; subsequent runs are user-driven.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const a = sideA.item;
  const b = sideB.item;
  const hasResult = a !== null && b !== null;
  // Combined verdict for a security, on the currently selected risk window.
  const overall = (item: CompareSide) => overallScore(item, riskForWindow(item.risk, windowKey));
  const windowLabel = RISK_WINDOWS.find((w) => w.key === windowKey)?.label ?? "1Y";
  const metricGroups: MetricGroup[] = hasResult
    ? [
        { label: "Market snapshot", rows: MARKET_ROWS },
        // Returns are rendered by the dedicated ReturnsByPeriod card (full period matrix
        // plus the Returns score), so they are intentionally not a group card here.
        ...(a.expense_ratio != null || b.expense_ratio != null
          ? [{
              label: "Fund costs",
              rows: FUND_COST_ROWS,
              score: fundCostScore,
              scoreHint: "Based on net expense ratio: 0% scores 100 and 1% or more scores 0.",
            }]
          : []),
        {
          label: "Risk",
          rows: riskRows(windowKey, windowLabel),
          score: (item) => riskScore(riskForWindow(item.risk, windowKey)),
          scoreHint: "Equal blend of annualized volatility and maximum drawdown; lower is better.",
        },
        // Risk-adjusted ratios are rendered by the dedicated RiskRatiosCard (ratios plus
        // their rolling sparklines and a diff column), so not a generic group card here.
      ]
    : [];

  return (
    <section className="space-y-4">
      <Card className="gap-3 py-3">
        <CardHeader className="py-0">
          <CardTitle className="text-base">Compare two securities</CardTitle>
          <CardDescription>
            Compare price, returns, risk, and trends.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void runCompare(symbolA, symbolB);
            }}
          >
            <Input
              value={symbolA}
              onChange={(e) => setSymbolA(e.target.value)}
              aria-label="compare-symbol-a"
              placeholder="e.g. SPY"
              className="h-9 w-28"
            />
            <ArrowLeftRight className="size-4 shrink-0 text-muted-foreground" />
            <Input
              value={symbolB}
              onChange={(e) => setSymbolB(e.target.value)}
              aria-label="compare-symbol-b"
              placeholder="e.g. QQQ"
              className="h-9 w-28"
            />
            <Button type="submit" size="sm" className="h-9" disabled={loading}>
              Compare
            </Button>
          </form>
          {status && <p className="mt-3 text-sm text-destructive">{status}</p>}
        </CardContent>
      </Card>

      {loading && (
        <Card className="gap-3 py-3">
          <CardContent className="space-y-2 pt-0" data-testid="compare-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </CardContent>
        </Card>
      )}

      {hasResult && !loading && preferencesLoaded && (
        <div className="space-y-3" data-testid="compare-table">
          {/* Risk window first: it drives the Risk and Risk-adjusted ratio cards and the
              overall score below, so it reads as the lens the whole comparison is viewed
              through. A window is disabled when neither security has enough history. */}
          <Card className="gap-2 py-2">
            <CardContent className="flex flex-wrap items-center gap-2 py-0">
              <span className="text-xs text-muted-foreground">Risk window</span>
              <div className="flex flex-wrap gap-1" role="tablist" aria-label="risk window">
                {RISK_WINDOWS.map((w) => {
                  const available =
                    hasWindow(riskForWindow(a.risk, w.key)) ||
                    hasWindow(riskForWindow(b.risk, w.key));
                  const active = w.key === windowKey;
                  return (
                    <button
                      key={w.key}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      disabled={!available}
                      aria-label={`risk-window-${w.key}`}
                      onClick={() => selectRiskWindow(w.key)}
                      className={
                        "rounded px-2 py-0.5 text-xs font-medium transition-colors " +
                        (active
                          ? "bg-primary/15 text-foreground"
                          : available
                            ? "text-muted-foreground hover:text-foreground"
                            : "cursor-not-allowed text-muted-foreground/40")
                      }
                    >
                      {w.label}
                    </button>
                  );
                })}
              </div>
              {windowKey === "max" &&
                (() => {
                  const starts = [
                    riskForWindow(a.risk, "max")?.start_date,
                    riskForWindow(b.risk, "max")?.start_date,
                  ]
                    .filter(Boolean)
                    .sort();
                  if (starts.length < 2) return null;
                  const span = sinceSpan(starts.at(-1));
                  return span ? (
                    <span className="text-xs text-muted-foreground">
                      Common history {span}
                    </span>
                  ) : null;
                })()}
              {preferenceStatus && (
                <span className="text-xs text-destructive">{preferenceStatus}</span>
              )}
            </CardContent>
          </Card>

          {/* Headline: the combined verdict, then the per-group score cards. */}
          <OverallScoreCard a={a} b={b} scoreA={overall(a)} scoreB={overall(b)} />

          {/* Each metric group as its own compact card, two per row on wider screens. */}
          <div className="grid gap-3 md:grid-cols-2">
            {metricGroups.map((group) => (
              <MetricGroupCard key={group.label} group={group} a={a} b={b} />
            ))}
          </div>

          {/* Returns is a scored card too, but full width since it carries the whole
              period matrix. It replaces the old "Returns" group card plus the separate
              "Returns by period" module, which showed the same data twice. */}
          <ReturnsByPeriod a={a} b={b} />

          {/* Risk-adjusted ratios: full width, carries the ratios plus rolling sparklines
              and a diff column. Replaces the old ratios group card and the separate
              "Risk-adjusted trends" module, which showed the same ratios twice. */}
          <RiskRatiosCard
            a={a}
            b={b}
            rows={ratioRows(windowKey, windowLabel)}
            windowKey={windowKey}
            windowLabel={windowLabel}
            scoreA={ratiosScore(riskForWindow(a.risk, windowKey))}
            scoreB={ratiosScore(riskForWindow(b.risk, windowKey))}
          />

          <p className="text-[10px] text-muted-foreground">
            Returns are YTD through Max; 10Y and Max are annualized. Risk figures use the
            selected window from daily total returns, risk-free rate 4.5%. Quality colors
            are rules of thumb, not advice. Group and overall scores are fixed 0–100 heuristics. The overall
            score is an equal-weight blend of the group scores on the selected window.
          </p>
        </div>
      )}

      {hasResult && !loading && preferencesLoaded && (
        <GrowthChart
          key={`${a.symbol}:${b.symbol}`}
          a={a}
          b={b}
          initialWindow={windowKey}
        />
      )}
      {hasResult && !loading && preferencesLoaded && <YearlyPerformance a={a} b={b} />}
      {hasResult && !loading && preferencesLoaded && <ValuationComparison a={a} b={b} />}
      {hasResult && !loading && preferencesLoaded && <ExpensivenessComparison a={a} b={b} />}
      {hasResult && !loading && preferencesLoaded && <RelativeValuationComparison a={a} b={b} />}
      {hasResult && !loading && preferencesLoaded && <TrendComparison a={a} b={b} />}
    </section>
  );
}
